import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { usersApi } from '../../services/api';
import { X, Loader2, Eye, EyeOff } from 'lucide-react';
import type { User, Role } from '../../types';

interface UserModalProps {
  user: User | null;
  roles: Role[];
  onClose: () => void;
  onSuccess: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ user, roles, onClose, onSuccess }) => {
  const isEditing = !!user;
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    role_id: '',
    first_name: '',
    last_name: '',
    is_active: true,
    assigned_states: [] as number[],
  });

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        password_confirmation: '',
        role_id: user.advisor?.role_id?.toString() || '',
        first_name: user.advisor?.first_name || '',
        last_name: user.advisor?.last_name || '',
        is_active: user.advisor?.is_active !== false,
        assigned_states: user.advisor?.assigned_states || [],
      });
    }
  }, [user]);

  // Check if the selected role is "Workflow"
  const selectedRole = roles.find(r => r.id === parseInt(formData.role_id));
  const isWorkflowRole = selectedRole?.name?.toLowerCase() === 'workflow';

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => usersApi.create({
      name: `${data.first_name} ${data.last_name}`,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      role_id: parseInt(data.role_id),
      code: data.email.split('@')[0].toUpperCase(),
      first_name: data.first_name,
      last_name: data.last_name,
      is_active: data.is_active,
    }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      const axiosErr = error as { response?: { data?: { errors?: Record<string, string[]> } } };
      if (axiosErr.response?.data?.errors) {
        setErrors(axiosErr.response.data.errors);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => usersApi.update(user!.id, {
      name: `${data.first_name} ${data.last_name}`,
      email: data.email,
      password: data.password || undefined,
      password_confirmation: data.password_confirmation || undefined,
      role_id: parseInt(data.role_id),
      first_name: data.first_name,
      last_name: data.last_name,
      is_active: data.is_active,
      assigned_states: data.assigned_states,
    }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      const axiosErr = error as { response?: { data?: { errors?: Record<string, string[]> } } };
      if (axiosErr.response?.data?.errors) {
        setErrors(axiosErr.response.data.errors);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleStateToggle = (stateNumber: number) => {
    setFormData(prev => ({
      ...prev,
      assigned_states: prev.assigned_states.includes(stateNumber)
        ? prev.assigned_states.filter(s => s !== stateNumber)
        : [...prev.assigned_states, stateNumber].sort((a, b) => a - b),
    }));
  };

  const workflowStates = [
    { number: 1, label: 'Nouveau client', color: 'bg-blue-500' },
    { number: 2, label: 'En attente documents', color: 'bg-yellow-500' },
    { number: 3, label: 'Validation', color: 'bg-orange-500' },
    { number: 4, label: 'Finalisation', color: 'bg-green-500' },
  ];

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Row 1: Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name[0]}</p>
              )}
            </div>
          </div>

          {/* Row 2: Email and Role */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rôle *
              </label>
              <select
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="">Sélectionner un rôle</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {errors.role_id && (
                <p className="mt-1 text-sm text-red-600">{errors.role_id[0]}</p>
              )}
            </div>
          </div>

          {/* Workflow State Assignment - only visible when editing and Workflow role selected */}
          {isEditing && isWorkflowRole && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                États assignés
              </label>
              <div className="grid grid-cols-2 gap-2">
                {workflowStates.map((state) => {
                  const isSelected = formData.assigned_states.includes(state.number);
                  return (
                    <button
                      key={state.number}
                      type="button"
                      onClick={() => handleStateToggle(state.number)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-3 h-3 rounded-full ${state.color}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-gray-700'}`}>
                        {state.label}
                      </span>
                      {isSelected && (
                        <svg className="w-4 h-4 text-primary-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Si aucun état n'est sélectionné, l'utilisateur verra tous les états.
              </p>
              {errors.assigned_states && (
                <p className="mt-1 text-sm text-red-600">{errors.assigned_states[0]}</p>
              )}
            </div>
          )}

          {/* Row 3: Password fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe {!isEditing && '*'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEditing}
                  placeholder={isEditing ? 'Laisser vide pour ne pas modifier' : ''}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmer le mot de passe {!isEditing && '*'}
              </label>
              <div className="relative">
                <input
                  type={showPasswordConfirm ? 'text' : 'password'}
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required={!isEditing && !!formData.password}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswordConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Utilisateur actif
              </span>
            </label>
            <p className="mt-1 text-sm text-gray-500 ml-7">
              Les utilisateurs inactifs ne peuvent pas se connecter au système.
            </p>
          </div>

          {/* Actions */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEditing ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
